import { useMemo, useState } from 'react'
import {
  buildChatUrl,
  buildEmbedUrl,
  extractId,
  type EmbedOptions,
} from './kinescopeEmbed'
import './KinescopeTestPage.css'

/**
 * Стенд для трансляций Kinescope.
 *
 * Нужен, чтобы вживую посмотреть, что умеет встроенный плеер: как ведёт себя
 * эфир, что происходит с чатом и работает ли вход в чат по токену. Страница
 * ничего не сохраняет и никуда не ходит — только собирает ссылки и показывает
 * их в iframe.
 */
const KinescopeTestPage = () => {
  const [source, setSource] = useState('')
  const [chatSource, setChatSource] = useState('')
  const [token, setToken] = useState('')
  const [options, setOptions] = useState<EmbedOptions>({
    autoplay: false,
    muted: false,
    hideControls: false,
  })

  const videoId = useMemo(() => extractId(source), [source])
  const embedUrl = useMemo(
    () => buildEmbedUrl(videoId, options),
    [videoId, options]
  )

  // Чат по умолчанию берём от того же эфира: у трансляции идентификатор общий.
  const chatId = useMemo(
    () => extractId(chatSource) || videoId,
    [chatSource, videoId]
  )
  const chatUrl = useMemo(() => buildChatUrl(chatId, token), [chatId, token])

  const toggle = (key: keyof EmbedOptions) =>
    setOptions(prev => ({ ...prev, [key]: !prev[key] }))

  return (
    <div className="kt-page">
      <h1 className="kt-title">Стенд Kinescope</h1>
      <p className="kt-subtitle">
        Вставьте код встраивания, ссылку или идентификатор — страница соберёт
        адрес и покажет плеер. Ничего не сохраняется.
      </p>

      <label className="kt-field">
        <span>Плеер: код встраивания, ссылка или id</span>
        <textarea
          value={source}
          onChange={event => setSource(event.target.value)}
          placeholder='<iframe src="https://kinescope.io/embed/0cEiz8x5fJLMcjb9Scii7d" …>'
          rows={3}
        />
      </label>

      <div className="kt-options">
        <label>
          <input
            type="checkbox"
            checked={options.autoplay}
            onChange={() => toggle('autoplay')}
          />
          автозапуск
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.muted}
            onChange={() => toggle('muted')}
          />
          без звука
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.hideControls}
            onChange={() => toggle('hideControls')}
          />
          скрыть управление
        </label>
      </div>

      {videoId ? (
        <>
          <p className="kt-url">{embedUrl}</p>
          <div className="kt-player">
            <iframe
              key={embedUrl}
              src={embedUrl}
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write; screen-wake-lock;"
              frameBorder="0"
              allowFullScreen
              title="Трансляция"
            />
          </div>
        </>
      ) : (
        source.trim() && (
          <p className="kt-warning">
            Не нашёл идентификатор. Нужна ссылка вида
            kinescope.io/embed/… или сам идентификатор.
          </p>
        )
      )}

      <h2 className="kt-section">Чат</h2>
      <p className="kt-subtitle">
        Чат живёт отдельным адресом, а не внутри плеера: чтобы его не было на
        странице, достаточно не встраивать этот блок. Токен нужен, только если
        у эфира включена авторизация в чате.
      </p>

      <label className="kt-field">
        <span>Эфир для чата (по умолчанию тот же, что у плеера)</span>
        <input
          value={chatSource}
          onChange={event => setChatSource(event.target.value)}
          placeholder="ссылка или id эфира"
        />
      </label>

      <label className="kt-field">
        <span>JWT-токен зрителя (необязательно)</span>
        <textarea
          value={token}
          onChange={event => setToken(event.target.value)}
          placeholder="eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9…"
          rows={2}
        />
      </label>

      {chatId && (
        <>
          <p className="kt-url">{chatUrl}</p>
          <div className="kt-chat">
            <iframe
              key={chatUrl}
              src={chatUrl}
              frameBorder="0"
              allow="clipboard-write"
              title="Чат трансляции"
            />
          </div>
        </>
      )}
    </div>
  )
}

export default KinescopeTestPage
